import type { StringDate } from "@/lib/string-date";
import type {
  LegalEntityId,
  PersonId,
  SiteId,
  SubscriberId,
  SubscriptionFailureCategoryId,
  SubscriptionFailureReasonId,
  SubscriptionId,
  SubscriptionPipelineId,
  SubscriptionTagId,
} from "@/types/common/ids";

import {
  SubscriptionFailureCategoryCode,
  SubscriptionFailureReasonCode,
} from "@/domains/subscription/subscription.codes.types";

export enum SubscriptionStatus {
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export enum InstallationStatus {
  REQUIRED = "REQUIRED",
  FAILED = "FAILED",
  COMPLETED = "COMPLETED",
  NOT_REQUIRED = "NOT_REQUIRED",
  NEW_INTERVENTION_REQUIRED = "NEW_INTERVENTION_REQUIRED",
}

export enum ProspectionStatus {
  REQUIRED = "REQUIRED",
  NOT_REQUIRED = "NOT_REQUIRED",
  COMPLETED = "COMPLETED",
}

export enum EligibilityConfirmationStatus {
  NOT_REQUIRED = "NOT_REQUIRED",
  REQUIRED = "REQUIRED",
  ELIGIBILITY_CONFIRMED = "ELIGIBILITY_CONFIRMED",
}

export enum StatusTransitionAction {
  COMPLETE_PROSPECTION = "COMPLETE_PROSPECTION",
  CONFIRM_ELIGIBILITY = "CONFIRM_ELIGIBILITY",
  COMPLETE_INSTALLATION = "COMPLETE_INSTALLATION",
  INSTALLATION_REQUIRE_NEW_INTERVENTION = "INSTALLATION_REQUIRE_NEW_INTERVENTION",
  FAIL_SUBSCRIPTION_IMMEDIATELY = "FAIL_SUBSCRIPTION_IMMEDIATELY",
  REOPEN_SUBSCRIPTION = "REOPEN_SUBSCRIPTION",
}

export enum ActionScope {
  PROSPECTION = "PROSPECTION",
  ELIGIBILITY_CONFIRMATION = "ELIGIBILITY_CONFIRMATION",
  INSTALLATION = "INSTALLATION",
  GLOBAL = "GLOBAL",
}

export enum AcquisitionChannel {
  DIGITAL_LEAD = "DIGITAL_LEAD",
  FULL_DIGITAL_SUBSCRIPTION = "FULL_DIGITAL_SUBSCRIPTION",
  DIRECT_ACQUISITION = "DIRECT_ACQUISITION",
  EXTERNAL_VENDOR = "EXTERNAL_VENDOR",
  LANDLORD = "LANDLORD",
}

export interface SubscriptionStatuses {
  status: SubscriptionStatus;
  installationStatus: InstallationStatus;
  prospectionStatus: ProspectionStatus;
  eligibilityConfirmationStatus: EligibilityConfirmationStatus;
}

export interface SubscriptionAvailableAction {
  action: StatusTransitionAction;
  available: boolean;
  scopes: ActionScope[];
}

export interface SubscriptionFailure {
  reason: string;
  category: string;
  operatorUsername: string;
  operatorId: PersonId;
  comment?: string;
}

export interface SubscriptionSubscriber {
  id: SubscriberId;
  firstName: string;
  lastName: string;
}

export interface SubscriptionPartner {
  id: LegalEntityId;
  note: string;
}

export interface SubscriptionTag {
  id: SubscriptionTagId;
  value: string;
  path?: string;
}

/**
 * Tags are rendered as their full breadcrumb, so they are ordered on the path — the backend orders
 * on the leaf value only. Sorting on every read *and* on the optimistic insert is what keeps a chip
 * from jumping once the refetch lands.
 */
export function sortSubscriptionTags(tags: SubscriptionTag[]): SubscriptionTag[] {
  return tags.toSorted((left, right) => (left.path ?? left.value).localeCompare(right.path ?? right.value));
}

export interface SubscriptionPipelineTag {
  id: SubscriptionTagId;
  parentId?: SubscriptionTagId;
  value: string;
  path: string;
  children: SubscriptionPipelineTag[];
}

export interface SubscriptionOnHoldPeriod {
  id: number;
  start: StringDate;
  end: StringDate;
  operatorUsername: string;
  operatorId: PersonId;
}

export enum DelayedClosureProcessingResult {
  NOT_CLOSABLE = "NOT_CLOSABLE",
  DUPLICATE = "DUPLICATE",
  CLOSABLE_AFTER_DELAY = "CLOSABLE_AFTER_DELAY",
  CLOSABLE_IMMEDIATELY = "CLOSABLE_IMMEDIATELY",
}

export interface SubscriptionDelayedClosureRequest {
  id: number;
  startExecutionDateTime: StringDate;
  scheduledClosureDateTime: StringDate;
  creationDatetime: StringDate;
  origin: string;
  operatorUsername: string;
  operatorId: PersonId;
  processingResult?: DelayedClosureProcessingResult;
  failureReasonCode: SubscriptionFailureReasonCode;
  failureReasonLabel: string;
}

export interface SubscriptionModel {
  id: SubscriptionId;
  pipelineId: SubscriptionPipelineId;
  statuses: SubscriptionStatuses;
  subscriber: SubscriptionSubscriber;
  siteId: SiteId;
  partner: SubscriptionPartner;
  acquisitionChannel: AcquisitionChannel;
  creationDatetime: StringDate;
  closureDatetime?: StringDate;
  tags: SubscriptionTag[];
  onHoldPeriods: SubscriptionOnHoldPeriod[];
  delayedClosureRequests: SubscriptionDelayedClosureRequest[];
  failure?: SubscriptionFailure;
  actions: SubscriptionAvailableAction[];
  subscriptionClosedAsConflict?: SubscriptionId;
}

export function hasAvailableSubscriptionAction(
  subscription: SubscriptionModel,
  action: StatusTransitionAction,
): boolean {
  return subscription.actions.some((availableAction) => availableAction.action === action && availableAction.available);
}

function isDelayedClosureRequestPending(request: SubscriptionDelayedClosureRequest): boolean {
  return (
    request.processingResult === undefined ||
    request.processingResult === DelayedClosureProcessingResult.CLOSABLE_AFTER_DELAY
  );
}

function latestDelayedClosureRequest(
  requests: SubscriptionDelayedClosureRequest[],
): SubscriptionDelayedClosureRequest | undefined {
  let latest: SubscriptionDelayedClosureRequest | undefined;

  for (const request of requests) {
    if (!latest || request.startExecutionDateTime.toDate() > latest.startExecutionDateTime.toDate()) {
      latest = request;
    }
  }

  return latest;
}

export function getPendingDelayedClosureRequest(
  subscription: SubscriptionModel,
): SubscriptionDelayedClosureRequest | undefined {
  if (subscription.statuses.status !== SubscriptionStatus.RUNNING) return undefined;

  return latestDelayedClosureRequest(subscription.delayedClosureRequests.filter(isDelayedClosureRequestPending));
}

export function getLiftedDelayedClosureRequest(
  subscription: SubscriptionModel,
): SubscriptionDelayedClosureRequest | undefined {
  if (subscription.statuses.status !== SubscriptionStatus.RUNNING) return undefined;
  if (getPendingDelayedClosureRequest(subscription)) return undefined;

  const latest = latestDelayedClosureRequest(subscription.delayedClosureRequests);

  return latest?.processingResult === DelayedClosureProcessingResult.NOT_CLOSABLE ? latest : undefined;
}

// Subscription pipeline

export enum FailureReferentialSpot {
  PHONING_DELAYED_KO = "PHONING_DELAYED_KO",
  PHONING_IMMEDIATE_KO = "PHONING_IMMEDIATE_KO",
}

export interface FailureReasonModel {
  id: SubscriptionFailureReasonId;
  name: string;
  code: SubscriptionFailureReasonCode;
  comment?: string;
}

export interface FailureCategoryModel {
  id: SubscriptionFailureCategoryId;
  name: string;
  code: SubscriptionFailureCategoryCode;
  comment?: string;
  reasons: FailureReasonModel[];
}

export interface SubscriptionPipelineFailuresModel {
  pipelineId: SubscriptionPipelineId;
  referential: Record<FailureReferentialSpot, FailureCategoryModel[]>;
}

// Search

export interface SubscriptionHistoryFailureAuthor {
  id: PersonId;
  firstName: string;
  lastName: string;
  login: string;
}

/**
 * History rows carry a different failure shape than {@link SubscriptionFailure}: no category label,
 * but the raw reason code and the full identity of the operator who declared it.
 */
export interface SubscriptionHistoryFailure {
  reasonCode: SubscriptionFailureReasonCode;
  reason: string;
  comment?: string;
  declaredBy?: SubscriptionHistoryFailureAuthor;
}

export interface SubscriptionDataTableRow {
  id: SubscriptionId;
  subscriberFirstName: string;
  subscriberLastName: string;
  status: SubscriptionStatus;
  pipelineId: SubscriptionPipelineId;
  creationDatetime: StringDate;
  closureDatetime?: StringDate;
  failure?: SubscriptionHistoryFailure;
}

export interface SubscriptionDataTableResult {
  totalRows: number;
  invalidFilters: Array<{
    name: string;
    code: "UNKNOWN_COLUMN" | "INVALID_VALUE" | "UNEXPECTED_FILTER_ERROR";
    message?: string;
  }>;
  rows: SubscriptionDataTableRow[];
}

// Request types

export enum DataTableFilterOperator {
  EQUALS = "EQUALS",
  NOT_EQUALS = "NOT_EQUALS",
  CONTAINS = "CONTAINS",
  CONTAINS_IGNORE_CASE = "CONTAINS_IGNORE_CASE",
  GREATER_THAN = "GREATER_THAN",
  LESS_THAN = "LESS_THAN",
  IN = "IN",
}

export enum SubscriptionHistoryFilterName {
  ID = "id",
  FIRSTNAME = "firstname",
  LASTNAME = "lastname",
  STATUS = "status",
  SUBSCRIBER_ID = "subscriberId",
}

export interface SubscriptionHistoryFilter {
  name: SubscriptionHistoryFilterName;
  operator: DataTableFilterOperator;
  value: string | number;
}

export interface DataTableSort {
  fieldName: string;
  direction: "ASC" | "DESC";
}

export interface SubscriptionHistoryQuery {
  page: number;
  pageSize: number;
  sorts: DataTableSort[];
  filters: SubscriptionHistoryFilter[];
}
